import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Users, Trophy, Clock } from 'lucide-react';
import Footer from '../components/common/Footer';

const About = () => {
  return (
    <div className='min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8'>
      <div className='max-w-4xl mx-auto'>
        {/* Header */}
        <div className='mb-8'>
          <Link
            to='/home'
            className='inline-flex items-center text-blue-600 hover:text-blue-800 mb-4'
          >
            <ArrowLeft className='h-4 w-4 mr-2' />
            Back to Home
          </Link>
          <h1 className='text-3xl font-bold text-gray-900 mb-2'>
            About PCSO E-Lotto
          </h1>
          <p className='text-gray-600'>
            Learn more about our platform and services
          </p>
        </div>

        {/* Content */}
        <div className='space-y-8'>
          {/* Mission Section */}
          <div className='bg-white rounded-lg shadow-sm p-8'>
            <h2 className='text-2xl font-semibold text-gray-900 mb-4'>
              Our Mission
            </h2>
            <p className='text-gray-700 leading-relaxed text-lg'>
              PCSO E-Lotto is dedicated to providing a secure, transparent, and
              accessible online lottery platform that brings the excitement of
              traditional lottery games to the digital age. We strive to create
              a responsible gaming environment that prioritizes player safety
              while supporting charitable causes through the Philippine Charity
              Sweepstakes Office (PCSO).
            </p>
          </div>

          {/* Features Grid */}
          <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
            <div className='bg-white rounded-lg shadow-sm p-6'>
              <div className='flex items-center mb-4'>
                <Shield className='h-8 w-8 text-blue-600 mr-3' />
                <h3 className='text-xl font-semibold text-gray-900'>
                  Secure & Safe
                </h3>
              </div>
              <p className='text-gray-700 leading-relaxed'>
                Our platform uses advanced security measures to protect your
                personal information and ensure fair play. All transactions are
                encrypted and processed through secure payment gateways.
              </p>
            </div>

            <div className='bg-white rounded-lg shadow-sm p-6'>
              <div className='flex items-center mb-4'>
                <Users className='h-8 w-8 text-blue-600 mr-3' />
                <h3 className='text-xl font-semibold text-gray-900'>
                  Community Focused
                </h3>
              </div>
              <p className='text-gray-700 leading-relaxed'>
                We believe in building a strong community of players while
                supporting charitable causes. A portion of proceeds goes to
                various social programs and initiatives that benefit Filipino
                communities.
              </p>
            </div>

            <div className='bg-white rounded-lg shadow-sm p-6'>
              <div className='flex items-center mb-4'>
                <Trophy className='h-8 w-8 text-blue-600 mr-3' />
                <h3 className='text-xl font-semibold text-gray-900'>
                  Fair & Transparent
                </h3>
              </div>
              <p className='text-gray-700 leading-relaxed'>
                All our games use certified random number generators to ensure
                fair outcomes. Draw results are published transparently and can
                be verified by all players.
              </p>
            </div>

            <div className='bg-white rounded-lg shadow-sm p-6'>
              <div className='flex items-center mb-4'>
                <Clock className='h-8 w-8 text-blue-600 mr-3' />
                <h3 className='text-xl font-semibold text-gray-900'>
                  24/7 Availability
                </h3>
              </div>
              <p className='text-gray-700 leading-relaxed'>
                Play your favorite lottery games anytime, anywhere. Our platform
                is available round the clock with customer support ready to
                assist you whenever needed.
              </p>
            </div>
          </div>

          {/* Services Section */}
          <div className='bg-white rounded-lg shadow-sm p-8'>
            <h2 className='text-2xl font-semibold text-gray-900 mb-6'>
              Our Services
            </h2>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
              <div>
                <h3 className='text-lg font-medium text-gray-800 mb-2'>
                  Online Lottery Games
                </h3>
                <p className='text-gray-700 mb-4'>
                  Play various PCSO lottery games including Lotto 6/42, Lotto
                  6/45, Lotto 6/49, Lotto 6/55, and more through our secure
                  online platform.
                </p>
              </div>
              <div>
                <h3 className='text-lg font-medium text-gray-800 mb-2'>
                  Real-time Results
                </h3>
                <p className='text-gray-700 mb-4'>
                  Get instant access to draw results and check your tickets
                  immediately after each draw. All results are verified and
                  official.
                </p>
              </div>
              <div>
                <h3 className='text-lg font-medium text-gray-800 mb-2'>
                  Account Management
                </h3>
                <p className='text-gray-700 mb-4'>
                  Manage your gaming account, view transaction history, set
                  spending limits, and track your gaming activity through our
                  user-friendly dashboard.
                </p>
              </div>
              <div>
                <h3 className='text-lg font-medium text-gray-800 mb-2'>
                  Secure Payments
                </h3>
                <p className='text-gray-700 mb-4'>
                  Make deposits and withdrawals through various secure payment
                  methods including bank transfers, e-wallets, and other trusted
                  payment providers.
                </p>
              </div>
            </div>
          </div>

          {/* Responsible Gaming Section */}
          <div className='bg-blue-50 rounded-lg p-8'>
            <h2 className='text-2xl font-semibold text-gray-900 mb-4'>
              Responsible Gaming
            </h2>
            <p className='text-gray-700 leading-relaxed mb-4'>
              We are committed to promoting responsible gaming practices. We
              encourage all players to:
            </p>
            <ul className='list-disc list-inside text-gray-700 space-y-2 ml-4 mb-4'>
              <li>Set budgets and stick to them</li>
              <li>Never chase losses or bet more than you can afford</li>
              <li>Take regular breaks from gaming</li>
              <li>Seek help if gaming becomes a problem</li>
            </ul>
            <p className='text-gray-700 leading-relaxed'>
              If you or someone you know needs help with gambling-related
              issues, please contact appropriate support services or reach out
              to us for assistance.
            </p>
          </div>

          {/* Company Information */}
          <div className='bg-white rounded-lg shadow-sm p-8'>
            <h2 className='text-2xl font-semibold text-gray-900 mb-4'>
              Company Information
            </h2>
            <div className='space-y-4'>
              <div>
                <h3 className='text-lg font-medium text-gray-800 mb-2'>
                  About PCSO
                </h3>
                <p className='text-gray-700 leading-relaxed'>
                  The Philippine Charity Sweepstakes Office (PCSO) is a
                  government-owned and controlled corporation of the
                  Philippines. It operates various lottery games and uses
                  proceeds to fund health programs, medical assistance, and
                  charities throughout the Philippines.
                </p>
              </div>
              <div>
                <h3 className='text-lg font-medium text-gray-800 mb-2'>
                  Our Platform
                </h3>
                <p className='text-gray-700 leading-relaxed'>
                  PCSO E-Lotto serves as the official online platform for PCSO
                  lottery games, providing players with convenient access to
                  authorized lottery games while maintaining the highest
                  standards of security and fairness.
                </p>
              </div>
            </div>
          </div>

          {/* Contact Section */}
          <div className='bg-white rounded-lg shadow-sm p-8'>
            <h2 className='text-2xl font-semibold text-gray-900 mb-4'>
              Get in Touch
            </h2>
            <p className='text-gray-700 leading-relaxed mb-4'>
              Have questions or need assistance? We're here to help!
            </p>
            <div className='space-y-2'>
              <p className='text-gray-700'>
                <strong>Email:</strong>{' '}
                <a
                  href='mailto:support@pcsoe-lotto.com'
                  className='text-blue-600 hover:text-blue-800 underline'
                >
                  support@pcsoe-lotto.com
                </a>
              </p>
              <p className='text-gray-700'>
                <strong>Customer Support:</strong> Available 24/7 through our
                platform
              </p>
            </div>
            <div className='mt-6'>
              <Link
                to='/contact'
                className='inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
              >
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default About;
